import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { ITEM_DEFINITIONS, LOOT_TABLES, CONSUMABLE_DEFINITIONS, CONSUMABLE_LOOT } from '../data/items';
import { rollExploreResources, formatResourceDrop } from '../utils/ResourceLoot';
import { SfxManager } from '../utils/SfxManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE D'EXPLORATION ===
// Mini-grille 5x5 où le joueur retourne des tuiles cachées.
// Nombre de fouilles limité (5 max). Trouver or, items, pièges ou rien.

const EXPLORE_GRID = 5;
const EXPLORE_TILE = 72;
const EXPLORE_ORIGIN_X = GAME_CX - (EXPLORE_GRID * EXPLORE_TILE) / 2;
const EXPLORE_ORIGIN_Y = 160;
const MAX_REVEALS = 5;

/** Types de contenu caché sous une tuile */
type TileContent = 'gold_small' | 'gold_big' | 'item' | 'consumable' | 'resource' | 'trap' | 'heal' | 'empty';

/** Poids de génération */
const CONTENT_WEIGHTS: { type: TileContent; weight: number }[] = [
  { type: 'empty', weight: 4 },
  { type: 'gold_small', weight: 4 },
  { type: 'gold_big', weight: 2 },
  { type: 'item', weight: 2 },
  { type: 'consumable', weight: 2 },
  { type: 'resource', weight: 4 },
  { type: 'trap', weight: 4 },
  { type: 'heal', weight: 3 },
];

const CONTENT_EMOJI: Record<TileContent, string> = {
  gold_small: '💰',
  gold_big: '💎',
  item: '🎁',
  consumable: '🧪',
  resource: '🪵',
  trap: '💀',
  heal: '❤️',
  empty: '💨',
};

export class ExploreScene extends Phaser.Scene {
  private run!: RunManager;
  private sfx = new SfxManager();
  private grid: TileContent[][] = [];
  private revealed: boolean[][] = [];
  private revealsLeft = MAX_REVEALS;
  private revealCountText!: Phaser.GameObjects.Text;
  private logText!: Phaser.GameObjects.Text;
  private goldText!: Phaser.GameObjects.Text;
  private pendingLog = '';

  constructor() {
    super({ key: 'ExploreScene' });
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
    this.revealsLeft = MAX_REVEALS;
    this.generateGrid();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Fond
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    // Titre
    this.add.text(GAME_CX, 30, '🔍 EXPLORATION', {
      fontSize: '22px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(GAME_CX, 58, 'Retourne les tuiles pour trouver du butin !', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5);

    // Compteur de fouilles
    this.revealCountText = this.add.text(GAME_CX, 85, '', {
      fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);
    this.updateRevealCount();

    // Or du joueur
    this.goldText = this.add.text(GAME_CX, 110, '', {
      fontSize: '13px', color: '#f39c12', fontFamily: FONT_BODY,
    }).setOrigin(0.5);
    this.updateGoldDisplay();

    // Grille
    this.drawGrid();

    // Log des trouvailles
    this.logText = this.add.text(GAME_CX, 560, '', {
      fontSize: '13px', color: '#ecf0f1', fontFamily: FONT_BODY, align: 'center',
      wordWrap: { width: Math.min(500, GAME_W - 30) },
    }).setOrigin(0.5);

    // Bouton quitter
    const leaveBtn = this.add.text(GAME_CX, 630, '[ 🗺️ Retour à la carte ]', {
      fontSize: '16px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 16, y: 8 },
    }).setOrigin(0.5);
    leaveBtn.setInteractive({ useHandCursor: true });
    leaveBtn.on('pointerdown', () => {
      this.run.completeCurrentNode();
      startSceneWithFade(this, 'MapScene', { run: this.run });
    });
    leaveBtn.on('pointerover', () => leaveBtn.setColor('#ff6b6b'));
    leaveBtn.on('pointerout', () => leaveBtn.setColor('#e74c3c'));
  }

  /** Génère le contenu caché de la grille */
  private generateGrid(): void {
    this.grid = [];
    this.revealed = [];
    const totalWeight = CONTENT_WEIGHTS.reduce((s, w) => s + w.weight, 0);

    for (let row = 0; row < EXPLORE_GRID; row++) {
      this.grid[row] = [];
      this.revealed[row] = [];
      for (let col = 0; col < EXPLORE_GRID; col++) {
        this.grid[row][col] = this.pickContent(totalWeight);
        this.revealed[row][col] = false;
      }
    }
  }

  private pickContent(totalWeight: number): TileContent {
    let roll = Math.random() * totalWeight;
    for (const w of CONTENT_WEIGHTS) {
      roll -= w.weight;
      if (roll <= 0) return w.type;
    }
    return 'empty';
  }

  private drawGrid(): void {
    for (let row = 0; row < EXPLORE_GRID; row++) {
      for (let col = 0; col < EXPLORE_GRID; col++) {
        const x = EXPLORE_ORIGIN_X + col * EXPLORE_TILE + EXPLORE_TILE / 2;
        const y = EXPLORE_ORIGIN_Y + row * EXPLORE_TILE + EXPLORE_TILE / 2;

        if (this.revealed[row][col]) {
          // Tuile révélée
          const content = this.grid[row][col];
          this.add.rectangle(x, y, EXPLORE_TILE - 4, EXPLORE_TILE - 4, 0x2a3a5c, 0.5)
            .setStrokeStyle(1, 0x4a4a6a);
          this.add.text(x, y, CONTENT_EMOJI[content], {
            fontSize: '24px', fontFamily: FONT_BODY,
          }).setOrigin(0.5).setAlpha(0.6);
        } else {
          // Tuile cachée - interactive
          const tile = this.add.rectangle(x, y, EXPLORE_TILE - 4, EXPLORE_TILE - 4, 0x1e2d4a)
            .setStrokeStyle(2, 0x3a5a8c);
          const questionMark = this.add.text(x, y, '?', {
            fontSize: '22px', color: '#4a6a9c', fontFamily: FONT_BODY, fontStyle: 'bold',
          }).setOrigin(0.5);

          if (this.revealsLeft > 0) {
            tile.setInteractive({ useHandCursor: true });
            tile.on('pointerover', () => {
              tile.setStrokeStyle(2, 0xf1c40f);
              questionMark.setColor('#f1c40f');
            });
            tile.on('pointerout', () => {
              tile.setStrokeStyle(2, 0x3a5a8c);
              questionMark.setColor('#4a6a9c');
            });
            tile.on('pointerdown', () => {
              this.revealTile(row, col);
            });
          }
        }
      }
    }
  }

  private revealTile(row: number, col: number): void {
    if (this.revealed[row][col] || this.revealsLeft <= 0) return;

    this.revealed[row][col] = true;
    this.revealsLeft--;
    this.sfx.select();
    this.run.getMeta().addStat('tilesExplored');

    const content = this.grid[row][col];
    this.applyContent(content);

    // Redessiner la grille
    this.children.removeAll(true);
    this.create();

    // Afficher le log après le redraw + animation flottante sur la tuile
    if (this.pendingLog) {
      this.logText.setText(this.pendingLog);

      // LOT C3 : flash sur le log pour attirer l'œil sur un nouveau gain
      const baseLogColor = this.logText.style.color as string;
      this.logText.setColor('#ffffff');
      this.tweens.add({
        targets: this.logText, alpha: { from: 0.5, to: 1 },
        duration: 200, yoyo: true,
        onComplete: () => this.logText.setColor(baseLogColor),
      });

      // Animation flottante au-dessus de la tuile révélée
      const tileX = EXPLORE_ORIGIN_X + col * EXPLORE_TILE + EXPLORE_TILE / 2;
      const tileY = EXPLORE_ORIGIN_Y + row * EXPLORE_TILE;
      // Couleur du texte flottant selon le type de contenu
      const floatColors: Record<TileContent, string> = {
        gold_small: '#f1c40f', gold_big: '#f39c12',
        item: '#3498db', consumable: '#9b59b6',
        resource: '#2ecc71', trap: '#e74c3c',
        heal: '#1abc9c', empty: '#7f8c8d',
      };
      // LOT C3 : empilement vertical si plusieurs gains rapprochés (yOffset
      // selon le nombre de floatTexts encore visibles à proximité)
      const yOffset = this.activeFloatCount * 16;
      this.activeFloatCount++;
      const floatText = this.add.text(tileX, tileY - yOffset, this.pendingLog, {
        fontSize: content === 'gold_big' ? '15px' : '13px',
        color: floatColors[content], fontFamily: FONT_BODY, fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(20);

      // Phase 1 : monte rapidement en restant opaque (600ms)
      this.tweens.add({
        targets: floatText, y: tileY - yOffset - 25,
        duration: 600, ease: 'Sine.easeOut',
      });
      // Phase 2 : hold 1500ms puis fade out 700ms = total ~2.8s visible
      this.tweens.add({
        targets: floatText, alpha: 0, y: tileY - yOffset - 55,
        duration: 700, delay: 2100, ease: 'Power2',
        onComplete: () => {
          floatText.destroy();
          this.activeFloatCount = Math.max(0, this.activeFloatCount - 1);
        },
      });

      this.pendingLog = '';
    }
  }

  /** Compteur de floatTexts actifs pour empiler les gains rapprochés (LOT C3) */
  private activeFloatCount = 0;

  private applyContent(content: TileContent): void {
    switch (content) {
      case 'gold_small':
        this.run.addGold(8);
        this.pendingLog = '💰 +8 Or !';
        break;
      case 'gold_big':
        this.run.addGold(20);
        this.pendingLog = '💎 Jackpot ! +20 Or !';
        break;
      case 'item': {
        const inv = this.run.getInventory();
        if (!inv.hasTransportSpace()) {
          this.pendingLog = '🎒 Sac plein ! Impossible de ramasser.';
          break;
        }
        const pool = [...LOOT_TABLES.common, ...LOOT_TABLES.uncommon];
        const itemId = pool[Math.floor(Math.random() * pool.length)];
        const def = ITEM_DEFINITIONS[itemId];
        inv.pickLootItem(itemId);
        this.run.getMeta().addStat('itemsLooted');
        this.pendingLog = `🎁 +1 ${def.emoji} ${def.name} !`;
        break;
      }
      case 'consumable': {
        const cInv = this.run.getInventory();
        const cPool = [...CONSUMABLE_LOOT.common, ...CONSUMABLE_LOOT.uncommon];
        const cId = cPool[Math.floor(Math.random() * cPool.length)];
        const cDef = CONSUMABLE_DEFINITIONS[cId];
        const added = cInv.addConsumable(cId);
        if (!added) {
          this.pendingLog = `🧪 Consommables pleins ! ${cDef.emoji} ${cDef.name} perdu.`;
        } else {
          this.pendingLog = `🧪 +1 ${cDef.emoji} ${cDef.name} !`;
        }
        break;
      }
      case 'resource': {
        const resDrop = rollExploreResources();
        this.run.getCamp().getResources().addMultiple(resDrop);
        this.pendingLog = `🪵 ${formatResourceDrop(resDrop)}`;
        break;
      }
      case 'trap': {
        const currentHp = this.run.getPlayerHp();
        if (currentHp > 0) {
          const dmg = 3 + Math.floor(Math.random() * 4);
          this.run.setPlayerHp(Math.max(1, currentHp - dmg));
          this.pendingLog = `💀 Piège ! -${dmg} HP !`;
          this.sfx.hit();
        }
        break;
      }
      case 'heal': {
        const hp = this.run.getPlayerHp();
        if (hp > 0) {
          const heal = 5 + Math.floor(Math.random() * 4);
          this.run.setPlayerHp(Math.min(hp + heal, this.run.getPlayerMaxHp()));
          this.pendingLog = `❤️ Herbes médicinales ! +${heal} HP`;
        }
        break;
      }
      case 'empty':
        this.pendingLog = '💨 Rien ici... Dommage.';
        break;
    }
  }

  private updateRevealCount(): void {
    this.revealCountText?.setText(`Fouilles restantes : ${this.revealsLeft}/${MAX_REVEALS}`);
  }

  private updateGoldDisplay(): void {
    this.goldText?.setText(`💰 ${this.run.getGold()} Or   ❤️ ${this.run.getPlayerHp() > 0 ? this.run.getPlayerHp() : '?'} HP`);
  }
}
